import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Container, TopBar, BrandLogo, IconsContainer, ProfileIcon, SettingsIcon,
  MainContent, HeaderContainer, Header, TabContainer, Tab,
  LanguageSelectorContainer, LanguageSelector, LanguageButton,
  ChangeLanguageButtonContainer, ChangeLanguageIcon, TextAreasContainer,
  TextAreaContainer, TextArea, TranslationAreaContainer, TranslationArea,
  FileUploadContainer, FileUploadBox, FileUploadText, FileBrowseButton,
  ProgressBarContainer, ProgressBar, ProgressText, CopyIcon,
  RobotContainer, RobotImage, RobotText,
  DropdownContainer, DropdownMenu, DropdownItem,
  ModalOverlay, ModalContainer, ModalHeader, CloseButton,
  SettingGroup, SettingLabel, Select, SaveButton, ModelDescription,
  SuccessToast, ErrorToast, ConfirmButton, ButtonGroup,
  MetricsContainer, MetricRow, MetricLabel, MetricValue
} from './translationStyles';

const TranslationPage = () => {
  // États pour gérer les langues d'entrée et de sortie
  const [inputLanguage, setInputLanguage] = useState('Auto');
  const [outputLanguage, setOutputLanguage] = useState('English');
  const [selectedTab, setSelectedTab] = useState('Files');
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFileType, setSelectedFileType] = useState('');
  const [translatedFileName, setTranslatedFileName] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [isDownloadReady, setIsDownloadReady] = useState(false);
  const [translationMetrics, setTranslationMetrics] = useState(null);
  const [detectedLanguage, setDetectedLanguage] = useState(null);

  // States for UI dropdowns and modals
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState(
    localStorage.getItem('selectedModel') || 'Gemini 2.5 Flash Lite'
  );
  const [username, setUsername] = useState('User');
  const [userMetrics, setUserMetrics] = useState(null);

  // Ref to store polling timeout ID for cancellation
  const pollingTimeoutRef = useRef(null);

  // Easter egg state
  const [showEasterEgg, setShowEasterEgg] = useState(false);

  // Available models for translation with descriptions
  const availableModels = [
    {
      name: 'Gemini 2.5 Flash Lite',
      description: 'Fast and cost-effective. Best for simple translations and high volume.',
      disabled: false
    },
    {
      name: 'Gemini 2.5 Flash',
      description: 'Balanced speed and quality. Good for most translation tasks.',
      disabled: true,
      disabledReason: 'Currently unavailable due to API issues'
    },
    {
      name: 'MarianMT',
      description: 'Offline model. Fast but lower quality. Used as automatic fallback.',
      disabled: true,
      disabledReason: 'Currently unavailable - use Gemini 2.5 Flash Lite instead'
    }
  ];

  // Get current model description
  const getCurrentModelDescription = () => {
    const model = availableModels.find(m => m.name === selectedModel);
    return model ? model.description : '';
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileDropdown]);

  // Error and success toast states
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Helper function to show error toast
  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorMessage(true);
    setTimeout(() => setShowErrorMessage(false), 4000);
  };

  // Format time in minutes and seconds
  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}min ${secs}s`;
  };

  const formatDisplayName = (userData) => {
    if (!userData) return 'User';

    if (userData.display_name) {
      return userData.display_name;
    }

    const formatFirstName = (value) => {
      if (!value) return '';
      return value
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
    };

    const firstName = formatFirstName(userData.first_name);
    const lastName = userData.last_name ? userData.last_name.toUpperCase() : '';
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName) {
      return firstName;
    }

    if (userData.username) {
      const localPart = userData.username.split('@')[0];
      const tokens = localPart.split(/[._-]+/).filter(Boolean);
      if (tokens.length >= 2) {
        return `${formatFirstName(tokens[0])} ${tokens[tokens.length - 1].toUpperCase()}`;
      }
      if (tokens.length === 1) {
        return formatFirstName(tokens[0]);
      }
      return userData.username;
    }

    return 'User';
  };

  // Handle robot click for easter egg
  const handleRobotClick = () => {
    setShowEasterEgg(true);
    setTimeout(() => setShowEasterEgg(false), 3000);
  };

  // Get contextual robot message based on current state
  const getRobotMessage = () => {
    // Easter egg message
    if (showEasterEgg) {
      return `Coucou mon tchoupinoux ! 💜`;
    }

    // Translation in progress
    if (isTranslating) {
      return `Your ${selectedFileType.toUpperCase()} file is being translated. Hang tight! You can cancel anytime using the button above.`;
    }

    // Translation completed
    if (isDownloadReady) {
      return `Translation complete! Your file is ready to download. Click the Download button above, or Retry to translate another file.`;
    }

    // File tab
    if (selectedTab === 'Files') {
      if (selectedFileName && !isTranslating) {
        return `File "${selectedFileName}" selected! Ready to translate from ${inputLanguage === 'Auto' ? 'auto-detected language' : inputLanguage} to ${outputLanguage}. Click Translate to begin.`;
      }
      if (inputLanguage === 'Auto') {
        return `Drag & drop your file to get started. I'll automatically detect the language from your document content and translate accordingly!`;
      }
      return `Drag & drop your PowerPoint (.pptx) or Word (.docx) file here, or browse your device. I'll translate it from ${inputLanguage} to ${outputLanguage}!`;
    }

    // Text tab
    if (selectedTab === 'Text') {
      if (inputText && inputText.length > 10) {
        if (inputLanguage === 'Auto') {
          if (detectedLanguage) {
            return `Great! I detected ${detectedLanguage} and will translate to ${outputLanguage}. Click Translate when ready!`;
          }
          return `Enter a bit more text so I can detect the language automatically, or select a specific input language.`;
        }
        return `Text ready! I'll translate from ${inputLanguage} to ${outputLanguage}. Click Translate to begin.`;
      }
      if (inputLanguage === 'Auto') {
        return `Type or paste your text here. I'll automatically detect whether it's French, English, or German, and translate accordingly!`;
      }
      return `Type or paste your text in the left box. I'll translate it from ${inputLanguage} to ${outputLanguage}.`;
    }

    // Default welcome message
    const currentModelInfo = availableModels.find(m => m.name === selectedModel);
    const modelDescription = currentModelInfo ? currentModelInfo.description : '';
    return `Welcome to your Gen AI translation tool developed by <span style="color: #5D33E2; font-weight: bold">Wavestone</span> using LiteLLM. Currently using <span style="color: #5D33E2; font-weight: bold">${selectedModel}</span> - ${modelDescription}`;
  };

  // Get username from localStorage on mount
  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    if (storedUsername) {
      setUsername(storedUsername);
    }
  }, []);

  // Fetch user metrics from API
  const fetchUserMetrics = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch('/user/metrics', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (handleAuthError(response.status)) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setUserMetrics(data);
        setUsername(formatDisplayName(data));
      } else {
        console.error('Failed to fetch user metrics');
      }
    } catch (error) {
      console.error('Error fetching user metrics:', error);
    }
  };

  // Fetch metrics when component mounts
  useEffect(() => {
    fetchUserMetrics();
  }, []);

  // Helper function to get fresh token from localStorage
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Helper function to handle authentication errors (401, 422)
  const handleAuthError = (status) => {
    if (status === 401 || status === 422) {
      // Clear localStorage and redirect to login
      localStorage.clear();
      showError('Your session has expired. Please log in again.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      return true;
    }
    return false;
  };

  // Helper function to map language names to ISO codes
  const getLanguageCode = (languageName) => {
    // If Auto is selected, return 'auto' for backend to handle
    if (languageName === 'Auto') {
      // For text translation, use detected language if available
      if (selectedTab === 'Text' && detectedLanguage) {
        languageName = detectedLanguage;
      } else {
        // For file translation, send 'auto' to backend
        return 'auto';
      }
    }

    const languageMap = {
      'French': 'fr',
      'English': 'eng',
      'German': 'de'
    };
    return languageMap[languageName] || languageName.toLowerCase();
  };

  // Simple language detection function
  const detectLanguage = (text) => {
    if (!text || text.length < 10) return null;

    const sample = text.toLowerCase().substring(0, 500);

    // French detection patterns
    const frenchPatterns = {
      words: ['le', 'la', 'les', 'un', 'une', 'des', 'et', 'est', 'dans', 'pour', 'que', 'qui', 'avec', 'sur', 'par', 'cette', 'sont', 'être', 'avoir', 'fait', 'peut', 'tout', 'nous', 'vous', 'mais', 'plus', 'sans', 'très'],
      chars: ['é', 'è', 'ê', 'à', 'ç', 'ù', 'â', 'î', 'ô', 'û']
    };

    // English detection patterns
    const englishPatterns = {
      words: ['the', 'and', 'is', 'in', 'to', 'of', 'for', 'that', 'this', 'with', 'from', 'are', 'was', 'were', 'been', 'have', 'has', 'had', 'can', 'will', 'would', 'should', 'could', 'may', 'might', 'must', 'which']
    };

    // German detection patterns
    const germanPatterns = {
      words: ['der', 'die', 'das', 'und', 'ist', 'in', 'zu', 'den', 'von', 'mit', 'ein', 'eine', 'nicht', 'auf', 'sich', 'für', 'werden', 'als', 'kann', 'wird', 'über', 'auch'],
      chars: ['ä', 'ö', 'ü', 'ß']
    };

    // Count matches for each language
    const scores = {
      French: 0,
      English: 0,
      German: 0
    };

    // Check French
    frenchPatterns.words.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      const matches = sample.match(regex);
      if (matches) scores.French += matches.length;
    });
    frenchPatterns.chars.forEach(char => {
      scores.French += (sample.match(new RegExp(char, 'g')) || []).length * 2;
    });

    // Check English
    englishPatterns.words.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      const matches = sample.match(regex);
      if (matches) scores.English += matches.length;
    });

    // Check German
    germanPatterns.words.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      const matches = sample.match(regex);
      if (matches) scores.German += matches.length;
    });
    germanPatterns.chars.forEach(char => {
      scores.German += (sample.match(new RegExp(char, 'g')) || []).length * 2;
    });

    // Find language with highest score
    const maxScore = Math.max(scores.French, scores.English, scores.German);
    if (maxScore === 0) return 'English'; // Default to English if no matches

    if (scores.French === maxScore) return 'French';
    if (scores.English === maxScore) return 'English';
    if (scores.German === maxScore) return 'German';

    return 'English'; // Fallback
  };

  // Auto-select output language based on detected input language
  const autoSelectOutputLanguage = (detectedLang) => {
    if (detectedLang === 'French') {
      setOutputLanguage('English');
    } else if (detectedLang === 'English') {
      setOutputLanguage('French');
    } else if (detectedLang === 'German') {
      setOutputLanguage('English');
    }
  };

  const logout = async () => {
    const token = getToken();
    if (!token) {
      window.location.href = '/';
      return;
    }

    try {
      const response = await fetch('/logout', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Handle auth errors
      if (handleAuthError(response.status)) {
        return;
      }

      if (response.ok) {
        localStorage.clear();
        window.location.href = '/';
      } else {
        console.error('Logout error');
        showError('Error when disconnecting. Please try again.');
      }
    } catch (error) {
      console.error('Logout request error:', error);
      // Force logout even if request fails
      localStorage.clear();
      window.location.href = '/';
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleSaveSettings = () => {
    localStorage.setItem('selectedModel', selectedModel);
    setShowSettingsModal(false);
    setSuccessMessage(`Settings saved! Using ${selectedModel}`);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  // Functions pour changer les langues d'entrée et de sortie
  const handleInputLanguageChange = (language) => {
    setInputLanguage(language);

    // Clear detected language when manually selecting a language
    if (language !== 'Auto') {
      setDetectedLanguage(null);
    }

    // If switching to Auto, detect language from current text
    if (language === 'Auto' && inputText) {
      const detected = detectLanguage(inputText);
      setDetectedLanguage(detected);
      autoSelectOutputLanguage(detected);
    }
  };

  const handleOutputLanguageChange = (language) => {
    setOutputLanguage(language);
  };

  // Detect language when input text changes and Auto is selected
  useEffect(() => {
    if (inputLanguage === 'Auto' && inputText && selectedTab === 'Text') {
      const detected = detectLanguage(inputText);
      if (detected && detected !== detectedLanguage) {
        setDetectedLanguage(detected);
        autoSelectOutputLanguage(detected);
      }
    }
  }, [inputText, inputLanguage, selectedTab]);

  // Fonction pour échanger les langues et les contenus des zones de texte
  const swapLanguagesAndText = () => {
    const tempText = inputText;

    // If Auto is selected, use the detected language for swapping
    let currentInputLang = inputLanguage;
    if (inputLanguage === 'Auto' && detectedLanguage) {
      currentInputLang = detectedLanguage;
    }

    setInputLanguage(outputLanguage);
    setOutputLanguage(currentInputLang);

    setInputText(outputText);
    setOutputText(tempText);

    // Clear detected language when swapping away from Auto
    setDetectedLanguage(null);
  };

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setSelectedFileName(file.name);
      setSelectedFileType(file.name.split('.').pop().toLowerCase());
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx']
    },
    multiple: false,
    noClick: true,
    noKeyboard: true
  });

  const handleFileUpload = async () => {
    if (!selectedFile) {
      showError('Please select a file first.');
      return;
    }

    if (selectedFileType !== 'pptx' && selectedFileType !== 'docx') {
      showError('Please select a valid file (pptx or docx).');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('input_lang', getLanguageCode(inputLanguage));
    formData.append('output_lang', getLanguageCode(outputLanguage));
    formData.append('model_name', selectedModel);
    
    setIsTranslating(true);
    setTranslationProgress(0); // Réinitialise la progression à 0
    setIsDownloadReady(false); // Désactive le bouton de téléchargement
    resetUserFile(); // Reset les paramètres du back
    pollProgress(); // Lance la vérification de la progression

    try {
      // Envoi du fichier vers le backend
      const response = await fetch(`/translate/file`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`
        },
        body: formData,
      });

      // Handle authentication errors
      if (handleAuthError(response.status)) {
        setIsTranslating(false);
        return;
      }

      // Vérification de la réponse du backend
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error:', errorData);
        showError(`Error: ${errorData.error || 'An unknown error occurred'}`);
        setIsTranslating(false);
        return;
      }

      // Récupérer et stocker le nom du fichier traduit depuis la réponse
      const responseData = await response.json();
      if (responseData.output_filename) {
        setTranslationProgress(100);
        setTranslatedFileName(responseData.output_filename);
        setTranslationMetrics({
          time: responseData.translation_time,
          cost: responseData.translation_cost,
          totalComponents: responseData.total_components,
          inputTokens: responseData.input_tokens,
          outputTokens: responseData.output_tokens,
          systemPromptTokens: responseData.system_prompt_tokens || 0
        });
        setIsDownloadReady(true); // Active le bouton de téléchargement
        setIsTranslating(false); // Désactive la traduction

        // Refresh user metrics after successful translation
        fetchUserMetrics();
      } else {
        console.error("Output filename not found in the response");
      }

    } catch (error) {
      // Gestion des erreurs de type réseau
      showError(`An error occurred: ${error.message}`);
      setIsTranslating(false);
    }
  };

  // Fonction pour reset les paramètres de user_file dans le back (utile car translate/file et translate/progress sont appelés en //)
  const resetUserFile = async () => {
    try {
      const response = await fetch(`/translate/reset`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      // Handle authentication errors
      if (handleAuthError(response.status)) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        console.log(data.message);
      } else {
        const errorData = await response.json();
        console.error("Error:", errorData.error);
      }
    } catch (error) {
      console.error("Network error:", error);
    }
  }

  // Fonction pour effectuer une requête GET avec le paramètre `fileType`
  const getProgress = async () => {
    try {
      const response = await fetch(`/translate/progress/?file_type=${selectedFileType}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      // Handle authentication errors
      if (handleAuthError(response.status)) {
        throw new Error('Authentication error');
      }

      if (!response.ok) {
        throw new Error('Error retrieving progress');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error retrieving progress:', error);
      throw error;
    }
  };

  // Fonction récursive pour vérifier la progression
  const pollProgress = useCallback(async () => {
    try {
      const data = await getProgress();
      if (data && data.percentage < 100) {
        setTranslationProgress(data.percentage);
        // Continue polling
        pollingTimeoutRef.current = setTimeout(pollProgress, 2000);
      } else if (data && data.percentage >= 100) {
        setTranslationProgress(100);
        setIsDownloadReady(true);
        setIsTranslating(false);
      }
    } catch (error) {
      console.error('Erreur lors du suivi de la progression :', error.message);
      setTranslationProgress(0);
      setIsDownloadReady(false);
      setIsTranslating(false);
    }
  }, [selectedFileType]);

  // Cleanup polling on unmount or when translation stops
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  const handleDownload = async () => {
    try {
      const response = await fetch(`/download-translated-file/?file_type=${selectedFileType}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getToken()}`
        },
      });

      // Handle authentication errors
      if (handleAuthError(response.status)) {
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Download failed');
      }

      // Création d'un blob à partir de la réponse
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // Création d'un lien temporaire pour le téléchargement
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = translatedFileName || 'translated_file';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Nettoyage de l'URL
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      showError(`Download failed: ${error.message}`);
    }
  };

  const handleRetry = () => {
    setIsDownloadReady(false);
    setIsTranslating(false);
    setSelectedFile(null);
    setSelectedFileName('');
    setSelectedFileType('');
    setTranslatedFileName('');
    setTranslationMetrics(null);
  };

  const handleCancelTranslation = async () => {
    try {
      // Clear polling timeout immediately
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }

      const response = await fetch('/translate/cancel', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (handleAuthError(response.status)) {
        return;
      }

      if (response.ok) {
        // Reset UI state
        setIsTranslating(false);
        setTranslationProgress(0);
        setIsDownloadReady(false);
        setSelectedFile(null);
        setSelectedFileName('');
        setSelectedFileType('');
        setTranslatedFileName('');
        setTranslationMetrics(null);

        setSuccessMessage('Translation cancelled successfully');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        showError('Failed to cancel translation');
      }
    } catch (error) {
      console.error('Error cancelling translation:', error);
      showError(`Failed to cancel translation: ${error.message}`);
    }
  };
  
  const translateText = async () => {
    // Validate Auto detection has occurred
    if (inputLanguage === 'Auto' && !detectedLanguage) {
      showError('Could not detect input language. Please enter more text or select a specific language.');
      return;
    }

    try {
      const response = await fetch('/translate/text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          input_text: inputText,
          input_lang: getLanguageCode(inputLanguage),
          output_lang: getLanguageCode(outputLanguage),
          model: selectedModel,
        }),
      });

      // Handle authentication errors
      if (handleAuthError(response.status)) {
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch');
      }

      const data = await response.json();
      setOutputText(data.output_text);

      // Keep profile metrics in sync after text translations as well.
      fetchUserMetrics();
    } catch (error) {
      console.error('Error:', error);
      showError('Translation failed. Please try again.');
    }
  };

  return (
    <Container>
      <TopBar>
        <BrandLogo src="wavestone-logo.png" alt="Brand Logo" />
        <IconsContainer>
          <DropdownContainer className="profile-dropdown">
            <ProfileIcon
              src="profile-icon.png"
              alt="Profile"
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              style={{ cursor: 'pointer' }}
            />
            {showProfileDropdown && (
              <DropdownMenu>
                <DropdownItem style={{ fontWeight: '600', color: '#5D33E2', cursor: 'default' }}>
                  <i className="bi bi-person-circle" style={{ fontSize: '16px' }}></i>
                  {username}
                </DropdownItem>

                {/* User Metrics Section */}
                {userMetrics && (
                  <div style={{
                    padding: '14px 18px',
                    borderTop: '1px solid #F5F5F5',
                    background: '#FAFAFA'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#757575',
                      marginBottom: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Translation Statistics
                    </div>
                    <div style={{ fontSize: '14px', color: '#333', lineHeight: '2' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span>Documents translated:</span>
                        <span style={{ fontWeight: '600', color: '#5D33E2' }}>
                          {userMetrics.total_documents_translated}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span>Total cost:</span>
                        <span style={{ fontWeight: '600', color: '#5D33E2' }}>
                          {userMetrics.total_translation_cost}€
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span>Input tokens:</span>
                        <span style={{ fontWeight: '600', color: '#5D33E2' }}>
                          {userMetrics.total_input_tokens.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span>Output tokens:</span>
                        <span style={{ fontWeight: '600', color: '#5D33E2' }}>
                          {userMetrics.total_output_tokens.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>System prompt tokens:</span>
                        <span style={{ fontWeight: '600', color: '#5D33E2' }}>
                          {userMetrics.system_prompt_tokens ? userMetrics.system_prompt_tokens.toLocaleString() : 0}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <DropdownItem onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right" style={{ fontSize: '16px' }}></i>
                  Logout
                </DropdownItem>
              </DropdownMenu>
            )}
          </DropdownContainer>
          <SettingsIcon
            src="settings-icon.png"
            alt="Settings"
            onClick={() => setShowSettingsModal(true)}
          />
        </IconsContainer>
      </TopBar>
      <MainContent>
        <HeaderContainer>
          <Header>What can I translate for you today?</Header>
        </HeaderContainer>
        <TextAreasContainer>
          <TextAreaContainer>
            <LanguageSelectorContainer>
              <LanguageSelector>
                <LanguageButton
                  selected={inputLanguage === 'Auto'}
                  onClick={() => handleInputLanguageChange('Auto')}
                  style={{ position: 'relative' }}
                >
                  Auto
                  {inputLanguage === 'Auto' && detectedLanguage && (
                    <span style={{
                      fontSize: '10px',
                      display: 'block',
                      marginTop: '2px',
                      color: inputLanguage === 'Auto' ? 'white' : '#5D33E2',
                      opacity: 0.9
                    }}>
                      ({detectedLanguage})
                    </span>
                  )}
                </LanguageButton>
                <LanguageButton
                  selected={inputLanguage === 'French'}
                  onClick={() => handleInputLanguageChange('French')}
                >
                  French
                </LanguageButton>
                <LanguageButton
                  selected={inputLanguage === 'English'}
                  onClick={() => handleInputLanguageChange('English')}
                >
                  English
                </LanguageButton>
                <LanguageButton
                  selected={inputLanguage === 'German'}
                  onClick={() => handleInputLanguageChange('German')}
                >
                  German
                </LanguageButton>
              </LanguageSelector>
            </LanguageSelectorContainer>
          </TextAreaContainer>
          <ChangeLanguageButtonContainer>
            <ChangeLanguageIcon className="bi bi-arrow-left-right" onClick={swapLanguagesAndText} />
          </ChangeLanguageButtonContainer>
          <TranslationAreaContainer>
            <LanguageSelectorContainer>
              <LanguageSelector>
                <LanguageButton 
                  selected={outputLanguage === 'French'} 
                  onClick={() => handleOutputLanguageChange('French')}
                >
                  French
                </LanguageButton>
                <LanguageButton 
                  selected={outputLanguage === 'English'} 
                  onClick={() => handleOutputLanguageChange('English')}
                >
                  English
                </LanguageButton>
                <LanguageButton 
                  selected={outputLanguage === 'German'} 
                  onClick={() => handleOutputLanguageChange('German')}
                >
                  German
                </LanguageButton>
              </LanguageSelector>
            </LanguageSelectorContainer>
          </TranslationAreaContainer>
        </TextAreasContainer>

        {/* Conditional Content Based on Selected Tab */}
        {selectedTab === 'Files' ? (
          <FileUploadContainer>
            {isDownloadReady ? (
              // Zone affichée après traduction (bouton Download et métriques)
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                {/* Bouton Download */}
                <FileBrowseButton
                  onClick={handleDownload}
                >
                  <i className="bi bi-download" style={{ marginRight: '10px' }}></i> Download
                </FileBrowseButton>
                {/* Nom du fichier traduit */}
                <FileUploadText style={{ marginTop: '20px' }}>
                  {translatedFileName}
                </FileUploadText>

                {/* Translation Metrics */}
                {translationMetrics && (
                  <MetricsContainer>
                    <MetricRow>
                      <MetricLabel>Processing Time:</MetricLabel>
                      <MetricValue>{formatTime(translationMetrics.time)}</MetricValue>
                    </MetricRow>
                    <MetricRow>
                      <MetricLabel>Translation Cost:</MetricLabel>
                      <MetricValue>{translationMetrics.cost}€</MetricValue>
                    </MetricRow>
                    {/* <MetricRow>
                      <MetricLabel>Components Translated:</MetricLabel>
                      <MetricValue>{translationMetrics.totalComponents}</MetricValue>
                    </MetricRow> */}
                    <MetricRow>
                      <MetricLabel>Input Tokens:</MetricLabel>
                      <MetricValue>{translationMetrics.inputTokens}</MetricValue>
                    </MetricRow>
                    <MetricRow>
                      <MetricLabel>Output Tokens:</MetricLabel>
                      <MetricValue>{translationMetrics.outputTokens}</MetricValue>
                    </MetricRow>
                    <MetricRow>
                      <MetricLabel>System Prompt Tokens:</MetricLabel>
                      <MetricValue>{translationMetrics.systemPromptTokens || 0}</MetricValue>
                    </MetricRow>
                  </MetricsContainer>
                )}
              </div>
            ) : (
              isTranslating ? (
                // Afficher uniquement le nom du fichier et la barre de progression
                <div style={{ textAlign: 'center' }}>
                  <FileUploadText>
                    <span style={{ color: '#5D33E2', fontWeight: 'bold' }}>
                      {selectedFileName}
                    </span>
                  </FileUploadText>
                  <ProgressBarContainer>
                    <ProgressBar style={{ width: `${translationProgress}%` }} />
                    <ProgressText>{translationProgress}%</ProgressText>
                  </ProgressBarContainer>
                  <FileBrowseButton
                    onClick={handleCancelTranslation}
                    style={{ marginTop: '20px', backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                  >
                    <i className="bi bi-x-circle" style={{ marginRight: '10px' }}></i> Cancel Translation
                  </FileBrowseButton>
                </div>
              ) : (
                // Affichage initial avec drag-and-drop
                <FileUploadBox {...getRootProps()}>
                  <input {...getInputProps()} />
                  <FileUploadText style={{ marginTop: '20px', color: isDragActive ? '#5D33E2' : '#757575', fontWeight: isDragActive ? 'bold' : 'normal' }}>
                    {isDragActive ? 'Drop your file here' : 'Drag & drop your file (.pptx or .docx)'}
                  </FileUploadText>
                  <FileUploadText>Or</FileUploadText>
                  <FileBrowseButton type="button" onClick={(e) => {
                    e.stopPropagation();
                    open();
                  }}>
                    <i className="bi bi-folder2-open" style={{ marginRight: '10px' }}></i> Browse my device
                  </FileBrowseButton>
                  <FileUploadText>Maximum file size: 100 MB</FileUploadText>
                  {selectedFileName && (
                    <FileUploadText>
                      <span style={{ color: '#5D33E2', fontWeight: 'bold' }}>Selected file: {selectedFileName}</span>
                    </FileUploadText>
                  )}
                </FileUploadBox>
              )
            )}
          </FileUploadContainer>
        ) : (
          <TextAreasContainer>
            <TextAreaContainer>
              <TextArea 
                placeholder="Write or copy the text you want to translate" 
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
              />
            </TextAreaContainer>
            <TranslationAreaContainer>
                <TranslationArea 
                    placeholder="Translation" 
                    value={outputText}
                    readOnly
                />
                <CopyIcon className="bi bi-copy" onClick={() => { navigator.clipboard.writeText(outputText); }} />
            </TranslationAreaContainer>
          </TextAreasContainer>
        )}

        {/* Bouton Translate ou Retry, en dessous de la zone */}
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          {isDownloadReady && selectedTab === 'Files' ? (
            // Bouton Retry (visible après traduction)
            <FileBrowseButton 
              onClick={handleRetry}
            >
              <i className="bi bi-arrow-counterclockwise" style={{ marginRight: '10px' }}></i> Retry
            </FileBrowseButton>
          ) : (
            !isTranslating && !isDownloadReady ? (
              // Bouton Translate (visible avant traduction)
              <FileBrowseButton 
                onClick={selectedTab === 'Text' ? translateText : handleFileUpload}
              >
                <i className="bi bi-translate" style={{ marginRight: '10px' }}></i> Translate
              </FileBrowseButton>
            ) : null
          )}
        </div>
      </MainContent>
      {/* Conteneur du robot */}
      <RobotContainer>
        <RobotText dangerouslySetInnerHTML={{ __html: getRobotMessage() }} />
        <RobotImage
          src="talking-wavebot.png"
          alt="Robot"
          onClick={handleRobotClick}
          style={{ cursor: 'pointer' }}
        />
      </RobotContainer>

      {/* Settings Modal */}
      {showSettingsModal && (
        <ModalOverlay onClick={() => setShowSettingsModal(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <span>
                <i className="bi bi-gear-fill" style={{ marginRight: '10px', color: '#5D33E2' }}></i>
                Settings
              </span>
              <CloseButton onClick={() => setShowSettingsModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <SettingGroup>
              <SettingLabel>Translation Model</SettingLabel>
              <Select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {availableModels.map((model) => (
                  <option
                    key={model.name}
                    value={model.name}
                    disabled={model.disabled}
                    style={{
                      color: model.disabled ? '#999' : 'inherit',
                      fontStyle: model.disabled ? 'italic' : 'normal'
                    }}
                  >
                    {model.name} {model.disabled ? '(Unavailable)' : ''}
                  </option>
                ))}
              </Select>
              <ModelDescription style={{
                color: availableModels.find(m => m.name === selectedModel)?.disabled ? '#dc3545' : '#757575'
              }}>
                {availableModels.find(m => m.name === selectedModel)?.disabled
                  ? availableModels.find(m => m.name === selectedModel)?.disabledReason
                  : getCurrentModelDescription()}
              </ModelDescription>
            </SettingGroup>

            <SaveButton onClick={handleSaveSettings}>
              <i className="bi bi-check-circle" style={{ marginRight: '8px' }}></i>
              Save Settings
            </SaveButton>
          </ModalContainer>
        </ModalOverlay>
      )}


      {/* Success Toast Notification */}
      {showSuccessMessage && (
        <SuccessToast>
          <i className="bi bi-check-circle-fill" style={{ fontSize: '18px' }}></i>
          {successMessage}
        </SuccessToast>
      )}

      {/* Error Toast Notification */}
      {showErrorMessage && (
        <ErrorToast>
          <i className="bi bi-exclamation-circle-fill" style={{ fontSize: '18px' }}></i>
          {errorMessage}
        </ErrorToast>
      )}
    </Container>
  );
};

export default TranslationPage;
